import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  ExternalLink,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface ConnectAccountDialogProps {
  businessId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const ConnectAccountDialog: React.FC<ConnectAccountDialogProps> = ({
  businessId,
  open,
  onOpenChange,
  onSuccess
}) => {
  const [connectingPlatform, setConnectingPlatform] = React.useState<SocialPlatform | null>(null);

  const platforms: { platform: SocialPlatform; available: boolean; comingSoon?: boolean; note?: string }[] = [
    { platform: 'instagram', available: true },
    { platform: 'facebook', available: true },
    { platform: 'linkedin', available: true, note: 'Text & images only' },
    { platform: 'tiktok', available: true, note: 'Video content only' },
  ];

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram':
        return Instagram;
      case 'facebook':
        return Facebook;
      case 'linkedin':
        return Linkedin;
      case 'tiktok':
        return Video;
    }
  };

  const handleConnect = async (platform: SocialPlatform) => {
    if (!businessId) {
      toast.error('No business selected');
      return;
    }

    setConnectingPlatform(platform);

    try {
      // Call the Edge Function to initiate OAuth
      const { data, error } = await supabase.functions.invoke('social-oauth-init', {
        body: {
          business_id: businessId,
          platform,
          redirect_uri: `${window.location.origin}/dashboard?oauth_callback=true`
        }
      });

      if (error) throw error;

      if (data?.auth_url) {
        // Redirect to OAuth provider
        window.location.href = data.auth_url;
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      toast.error('Failed to connect', {
        description: 'Could not initiate connection. Please try again.'
      });
      setConnectingPlatform(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect Social Media Account</DialogTitle>
          <DialogDescription>
            Choose a platform to connect. You'll be redirected to authorize the connection.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 mt-4">
          {platforms.map(({ platform, available, comingSoon, note }) => {
            const Icon = getPlatformIcon(platform);
            const info = PLATFORM_INFO[platform];
            const isConnecting = connectingPlatform === platform;

            return (
              <Card
                key={platform}
                className={`cursor-pointer transition-all ${
                  available
                    ? 'hover:border-primary/50 hover:shadow-sm'
                    : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => available && !isConnecting && handleConnect(platform)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="rounded-lg p-2"
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <Icon
                          className="h-6 w-6"
                          style={{ color: info.color }}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{info.name}</p>
                          {note && (
                            <Badge variant="outline" className="text-xs font-normal">
                              {note}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {platform === 'instagram' || platform === 'facebook'
                            ? 'Posts, DMs, Comments'
                            : platform === 'linkedin'
                            ? 'Posts, Articles'
                            : 'Video Posts'}
                        </p>
                      </div>
                    </div>

                    {comingSoon ? (
                      <Badge>Coming Soon</Badge>
                    ) : isConnecting ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <Button variant="ghost" size="sm" className="gap-1">
                        Connect
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> You'll be redirected to the platform to authorize access.
            We only request the minimum permissions needed to post content and read messages.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectAccountDialog;
